import Vue from 'vue'
import { store } from '_/vuex/store'
import { mapGetters } from 'vuex'
import advancedSearchFactory from '_/vuex/modules/advancedSearch'
import _ from 'lodash'
import adminFactory from '_/vuex/modules/admin'
import i18n from '_/i18n'
import { getEntryFields } from '_/components/Admin/Admin.fields'
import { pError } from '_/helpers/logger'
const Modal = Vue.component(
  'Modal',
  require('_/components/UserInterface/Modal/Modal.vue').default
)
const Search = Vue.component(
  'Search',
  require('_/components/Admin/Search/Search.vue').default
)

const AdvancedSearch = Vue.component(
  'AdvancedSearch',
  require('_/components/UserInterface/Sidebar/AdminSidebar/AdvancedSearch/AdvancedSearch.vue')
    .default
)

export default {
  /*
   * Define props
   *
   * @type  {Object}
   */
  props: {
    moduleTableName: {
      type: String,
      required: true,
    },
    relatedTableNames: {
      type: Array,
      default: [],
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      /**
       * Advanced search fields
       *
       * @type {array}
       */
      fields: [],

      /**
       * Selected references
       *
       * @type  {array}
       */
      selectedReferences: [],
    }
  },

  /**
   * Set the components
   *
   * @return  {object}
   */
  components: {
    AdvancedSearch,
    Modal,
    Search,
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      photonModule: 'photonModule/photonModule',
    }),

    /**
     * Admin vuex store fetcher
     *
     * @returns Object
     */
    admin() {
      const getterName = `${this.adminModuleName}/${this.adminModuleName}`
      return store.getters[getterName]
    },

    /**
     * Advanced Search module getter
     *
     * @return  {object}
     */
    advancedSearch() {
      const getterName = `${this.vuexModuleName}/${this.vuexModuleName}`

      return store.getters[getterName]
    },

    /**
     * Returns the number of selected references
     *
     * @returns integer
     */
    selectedReferencesCount() {
      return this.selectedReferences.length
    },

    /**
     * vuexModuleName getter
     *
     * @return  {string}
     */
    vuexModuleName() {
      return `${this.moduleTableName}-reference-advanced-search-${Date.now()}`
    },

    /**
     * Fetches the selected module name
     *
     * @return string
     */
    selectedModule() {
      return this.admin.selectedModule.table_name
    },

    /**
     * adminModuleName getter
     *
     * @return  {string}
     */
    adminModuleName() {
      return `${
        this.moduleTableName
      }-reference-advanced-search-admin-${Date.now()}`
    },
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    /**
     * Toggles the reference link
     *
     * @param   {object}  entry
     * @return  {void}
     */
    toggleLinkReference({ entry } = {}) {
      const existingReferenceIndex = _.findIndex(
        this.selectedReferences,
        selectedReference => {
          return selectedReference.system.id === entry.system.id
        }
      )

      if (existingReferenceIndex > -1) {
        return this.clearReference(entry)
      }

      return this.linkReference(entry)
    },

    /**
     * Removes a selected single reference
     *
     * @param   {integer}  reference
     * @return  {void}
     */
    clearReference(reference) {
      const selectedReferencesIndex = _.findIndex(
        this.selectedReferences,
        selectedReference => {
          return selectedReference.system.id === reference.system.id
        }
      )

      this.selectedReferences.splice(selectedReferencesIndex, 1)
    },

    /**
     * Links a reference and hides the reference panel, if opened.
     *
     * @param   {object}  reference
     * @return  {void}
     */
    linkReference(reference = null) {
      if (!reference) {
        return
      }

      const existingReferenceIndex = _.findIndex(
        this.selectedReferences,
        selectedReference => {
          return (
            _.get(selectedReference, 'system.id', null) ===
            _.get(reference, 'system.id', null)
          )
        }
      )

      if (existingReferenceIndex > -1) {
        this.selectedReferences[existingReferenceIndex] = _.cloneDeep(reference)
      } else {
        this.selectedReferences.push(reference)
      }
    },

    /**
     * Emits the closeReferenceSelector event
     *
     * @return  {void}
     */
    closeReferenceSelector() {
      this.$emit('closeReferenceSelector')
    },

    /**
     * Emits the closeAndInsertReferences event
     *
     * @return  {void}
     */
    closeAndInsertReferences() {
      this.$emit('closeAndInsertReferences', this.selectedReferences)
    },

    /**
     * Bootstraps the admin view to be able to get the fields
     *
     * @param   {integer}  moduleEntryId
     * @return  {void}
     */
    bootstrap(moduleEntryId = null) {
      store
        .dispatch(`${this.adminModuleName}/adminBootstrap`, {
          moduleEntryId,
          moduleTableName: this.moduleTableName,
          shouldFetchSearch: false,
        })
        .then(() => {
          this.fields = getEntryFields(this)

          this.resetFormFields()
        })
        .catch(error => {
          pError('Reference view bootstrap action failed with an error', error)
        })
    },

    resetFormFields() {
      //
    },
  },

  /**
   * Set the created hook
   *
   * @return  {void}
   */
  created() {
    if (!_.has(store.state, this.vuexModuleName)) {
      store.registerModule(
        this.vuexModuleName,
        advancedSearchFactory(this.vuexModuleName)
      )
    }

    if (!_.has(store.state, this.adminModuleName)) {
      store.registerModule(
        this.adminModuleName,
        adminFactory(this.adminModuleName)
      )

      store
        .dispatch(`${this.adminModuleName}/setIsReference`, { value: true })
        .then(() => {
          this.bootstrap(null)
        })
    }
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    const payload = this.advancedSearch.payload
    store.dispatch(`${this.vuexModuleName}/filterModuleEntries`, {
      moduleTableName: this.moduleTableName,
      payload,
    })
  },
}
